import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { formatCurrency } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { ExternalLink, Check, Clock, Megaphone } from "lucide-react";

interface OfferRow {
  id: number;
  name: string;
  status: string;
  visibility: string;
  conversionMethod: string;
  payout: { payoutType: string; payoutAmount: number } | null;
  isAssigned: boolean;
}

export function AffOffersPage() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["aff-portal", "offers"],
    queryFn: async () => {
      const res = await api.get("/v1/portal/affiliate/offers");
      if (!res.ok) throw new Error("Failed to load offers");
      return (await res.json()).data as OfferRow[];
    },
  });

  const applyMut = useMutation({
    mutationFn: async (offerId: number) => {
      const res = await api.post(`/v1/portal/affiliate/offers/${offerId}/apply`);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to apply");
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["aff-portal", "offers"] }),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Available Offers</h1>
        <p className="text-sm text-gray-500 mt-1">Browse and apply for offers to start promoting</p>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse h-20" />)}
        </div>
      ) : !data?.length ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <Megaphone className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No offers available at the moment.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {data.map((offer) => (
            <div key={offer.id} className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Link
                    to="/portal/affiliate/offers/$offerId"
                    params={{ offerId: String(offer.id) }}
                    className="text-sm font-semibold text-gray-900 hover:text-emerald-700 transition-colors"
                  >
                    {offer.name}
                  </Link>
                  <span className={cn(
                    "text-[10px] font-medium px-1.5 py-0.5 rounded uppercase tracking-wide",
                    offer.visibility === "public" ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700",
                  )}>
                    {offer.visibility}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {offer.conversionMethod} &middot; ID: {offer.id}
                </p>
              </div>

              <div className="text-right shrink-0">
                {offer.payout && (
                  <p className="text-sm font-bold text-emerald-600">
                    {offer.payout.payoutType === "cpa_percentage"
                      ? `${offer.payout.payoutAmount}%`
                      : formatCurrency(offer.payout.payoutAmount)}
                    <span className="text-xs font-normal text-gray-400 ml-1">{offer.payout.payoutType.toUpperCase()}</span>
                  </p>
                )}
              </div>

              <div className="shrink-0">
                {offer.isAssigned ? (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2.5 py-1.5 rounded-lg">
                    <Check className="w-3 h-3" /> Approved
                  </span>
                ) : (
                  <button
                    onClick={() => applyMut.mutate(offer.id)}
                    disabled={applyMut.isPending}
                    className="text-xs font-medium px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                  >
                    {applyMut.isPending ? "Applying..." : "Apply"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
